<template>
  <div class="answered discussion discussion-answered pa-2">
    <v-progress-linear
      v-if="localIsProcessingData"
      indeterminate
      class="discussion-progress-bar mt-0"
    />
    <v-layout
      v-if="discussionRole !== 'inactive'"
      align-center
    >
      <v-flex
        :xs6="!isExternallyRestrictingCommentsByStudentId"
        :xs10="(
          isExternallyRestrictingCommentsByStudentId
          && userType === 'teacher'
        )"
        :xs12="(
          isExternallyRestrictingCommentsByStudentId
          && userType !== 'teacher'
        )"
        pl-1
        pr-2
      >
        <v-text-field
          clearable
          label="Search discussion"
          prepend-inner-icon="search"
          v-model="searchTerm"
          @click:prepend-inner="getSearchResultComments"
          @keyup.enter="getSearchResultComments"
          :disabled="localIsProcessingData"
        ></v-text-field>
      </v-flex>
      <v-flex
        v-if="(
          !isExternallyRestrictingCommentsByStudentId
          || userType === 'teacher'
        )"
        :xs6="!isExternallyRestrictingCommentsByStudentId"
        :xs2="isExternallyRestrictingCommentsByStudentId"
        pr-1
        pl-2
      >
        <v-layout align-center>
          <v-btn
            v-if="userType === 'teacher'"
            icon
            @click="downloadComments"
            :disabled="localIsProcessingData"
          >
            <v-icon>
              file_download
            </v-icon>
          </v-btn>
          <v-icon
            v-if="!isExternallyRestrictingCommentsByStudentId"
            :color="selectedCommenter !== null ? 'primary' : 'inactive'"
            class="mr-2"
            :disabled="localIsProcessingData"
          >
            filter_list
          </v-icon>
          <b-form-select
            v-if="!isExternallyRestrictingCommentsByStudentId"
            aria-label="Filter Comments by Commenter"
            v-model="selectedCommenter"
            @change="getSearchResultComments"
            :disabled="localIsProcessingData"
          >
            <option :value="null">All students</option>
            <option
              v-for="(commenter, commenterIndex) in commenters"
              :key="commenterIndex"
              :value="commenter"
            >
              {{commenter.firstName}} {{commenter.lastName}}
            </option>
          </b-form-select>
        </v-layout>
      </v-flex>
    </v-layout>
    <div
      class="pp-interaction-title"
      :class="{
        'mt-2': discussionRole === 'inactive'
      }"
      v-html="localInteraction.interactionText"
    />
    <v-layout
      v-if="!['inactive'].includes(discussionRole)"
      column
      class="discussion-comments-wrapper"
    >
      <template v-if="isSearchingForComments">
        <div
          v-if="hasExecutedSearchQuery"
          class="body-1 grey--text text--lighten-1 text-xs-center"
        >
          <template v-if="filteredSearchResultComments.length">
            Showing comments/replies
            <span v-if="(
              previousSearchResultOptions.searchTerm
              && previousSearchResultOptions.searchTerm.length
            )">
              for "{{previousSearchResultOptions.searchTerm}}"
            </span>
            <span v-if="externallyRestrictedCommenter">
              that are owned by {{externallyRestrictedCommenter.firstName}} {{externallyRestrictedCommenter.lastName}}
            </span>
            <span v-else-if="$_.get(previousSearchResultOptions, 'selectedCommenter.id')">
              that are owned by {{previousSearchResultOptions.selectedCommenter.firstName}} {{previousSearchResultOptions.selectedCommenter.lastName}}
            </span>
          </template>
          <template v-else>
            <span>No results.</span>
          </template>
        </div>
        <DiscussionPage
          v-for="(comments, pageIndex) in filteredSearchResultComments"
          :key="pageIndex"
          v-bubble.snackbar.error
          @refresh-discussion-comments="refreshDiscussion"
          @delete-comment="deleteComment"
          @populate-comments="populateComments"
          v-bind="$props"
          :is-processing-data.sync="localIsProcessingData"
          :contains-search-results="true"
          :page-index="pageIndex"
          :comments="comments"
          :interaction.sync="localInteraction"
          :interaction-attempt.sync="localInteractionAttempt"
          :root-teacher="rootTeacher"
        />
        <v-layout
          class="load-more-button-wrapper"
          justify-center
        >
          <v-btn
            v-if="(
              showLoadMoreButtonForSearchResultComments
              && discussionRole !== 'anonymous'
              && filteredSearchResultComments.length
            )"
            block
            flat
            @click="getNextSearchResultsPage"
          >
            Load more
          </v-btn>
        </v-layout>
      </template>
      <template v-else>
        <DiscussionPage
          v-for="(comments, pageIndex) in filteredComments"
          :key="pageIndex"
          v-bubble.snackbar.error
          @refresh-discussion-comments="refreshDiscussion"
          @delete-comment="deleteComment"
          @populate-comments="populateComments"
          v-bind="$props"
          :is-processing-data.sync="localIsProcessingData"
          :page-index="pageIndex"
          :comments="comments"
          :interaction.sync="localInteraction"
          :interaction-attempt.sync="localInteractionAttempt"
          :root-teacher="rootTeacher"
        />
        <v-layout
          class="load-more-button-wrapper"
          justify-center
        >
          <v-btn
            v-if="discussionRole !== 'anonymous'"
            block
            flat
            @click="getNextPage"
            :disabled="localIsProcessingData"
          >
            Load more
          </v-btn>
        </v-layout>
      </template>
      <div ref="intersectionObserverTarget" />
    </v-layout>
    <DiscussionInput
      v-if="!['inactive', 'observer'].includes(discussionRole)"
      v-bubble.snackbar.error
      @refresh-discussion-comments="refreshDiscussion"
      @populate-comments="populateComments"
      @delete-comment="deleteComment"
      v-bind="$props"
      :interaction.sync="localInteraction"
      :interaction-attempt.sync="localInteractionAttempt"
      :is-processing-data.sync="localIsProcessingData"
      :text-model.sync="commentText"
      :root-teacher="rootTeacher"
      mode='comment'
      :placeholder="commentInputPlaceholder"
      aria-label="Input field for new comment text."
    />
  </div>
</template>

<script>
import _ from 'lodash';
import api from '../../../services/api';
import Interaction from '../../../mixins/Interaction';
import DiscussionPage from './DiscussionPage.vue';
import DiscussionInput from './DiscussionInput.vue';

export default {
  name: 'Discussion',
  components: {
    DiscussionPage,
    DiscussionInput,
  },
  mixins: [
    Interaction,
  ],
  props: [
    'anonymous',
  ],
  data: () => ({
    isReadyForIntersectionObserverEvents: false,
    hasExecutedSearchQuery: false,
    page: 0,
    searchResultComments: [],
    comments: [],
    commentText: '',
    searchTerm: '',
    selectedCommenter: null,
    previousSearchResultOptions: {
      searchTerm: '',
      selectedCommenter: null,
    },
    showLoadMoreButtonForSearchResultComments: true,
    commenters: [],
    test: true,
  }),
  computed: {
    commentInputPlaceholder() {
      if (['inactive', 'observer'].includes(this.discussionRole)) return '';
      return `Posting as ${this.discussionRole === 'anonymous' ? 'Anonymous User' : `${this.user.firstName} ${this.user.lastName}`}`;
    },
    commentsAndReplies() {
      return this.$_(this.comments)
        .map((comment) => [comment, ...(comment.replies || [])])
        .flattenDeep()
        .value();
    },
    isExternallyRestrictingCommentsByStudentId() {
      return (
        this.$_.isFinite(this.restrictDiscussionCommentsByStudentId)
        || (
          this.$_.isArray(this.restrictDiscussionCommentsByStudentId)
          && this.restrictDiscussionCommentsByStudentId.length
        )
      );
    },
    externallyRestrictedCommenter() {
      return this.commenters.find((commenter) => _.get(commenter, 'id') === this.restrictDiscussionCommentsByStudentId);
    },
    lastComment() {
      return this.comments[this.comments.length - 1];
    },
    lastSearchResultComment() {
      return this.searchResultComments[this.searchResultComments.length - 1];
    },
    isSearchingForComments() {
      return (
        (
          this.$_.isString(this.searchTerm)
          && !!this.searchTerm.length
        )
        || !!_.get(this, 'selectedCommenter.id')
        || this.isExternallyRestrictingCommentsByStudentId
      );
    },
    haveSearchOptionsBeenChanged() {
      return (
        this.previousSearchResultOptions.searchTerm !== this.searchTerm
        || _.get(this, 'previousSearchResultOptions.selectedCommenter.id') !== _.get(this, 'selectedCommenter.id')
      );
    },
    filteredComments() {
      return this.$_(this.comments)
        .filter((comment) => {
          const conditions = [comment.displayTime <= this.maxDisplayTime];
          if (this.$_.isFinite(this.restrictDiscussionCommentsByStudentId)) {
            // Filter comments down to a single student based on values that are passed into this component.
            conditions.push(comment.student.id === this.restrictDiscussionCommentsByStudentId);
          } else if (
            this.$_.isArray(this.restrictDiscussionCommentsByStudentId)
            && this.restrictDiscussionCommentsByStudentId.length
          ) {
            // Filter comments down to multiple students based on values that are passed into this component.
            conditions.push(this.restrictDiscussionCommentsByStudentId.includes(comment.student.id));
          }
          return conditions.every((condition) => condition);
        })
        .chunk(this.$constants.discussionCommentPageSize)
        .value();
    },
    filteredSearchResultComments() {
      return this.$_(this.discussionRole === 'anonymous' ? this.commentsAndReplies : this.searchResultComments)
        .filter((comment) => {
          const conditions = [
            comment.displayTime <= this.maxDisplayTime,
          ];
          if (this.$_.isFinite(this.restrictDiscussionCommentsByStudentId)) {
            // Filter comments down to a single student based on values that are passed into this component.
            conditions.push(comment.student.id === this.restrictDiscussionCommentsByStudentId);
          } else if (
            this.$_.isArray(this.restrictDiscussionCommentsByStudentId)
            && this.restrictDiscussionCommentsByStudentId.length
          ) {
            // Filter comments down to multiple students based on values that are passed into this component.
            conditions.push(this.restrictDiscussionCommentsByStudentId.includes(comment.student.id));
          }
          if (this.discussionRole === 'anonymous') {
            if (this.searchTerm.length) {
              conditions.push(comment.text.toLowerCase().includes(this.searchTerm.toLowerCase()));
            }
            if (this.selectedCommenter) {
              conditions.push(comment.student.id === this.selectedCommenter.id);
            }
          }
          return conditions.every((condition) => condition);
        })
        .chunk(this.$constants.discussionCommentPageSize)
        .value();
    },
    highestCommentId() {
      return _.get(this.$_.maxBy(this.comments, 'id'), 'id');
    },
  },
  methods: {
    async getStudentsInSection() {
      try {
        if (this.discussionRole === 'anonymous') {
          this.$set(this, 'commenters', [{
            id: 1,
            firstName: 'Anonymous',
            lastName: 'User',
          }]);
          return this.commenters;
        }
        const { data: students } = await api.getStudentsInSection(api.getSectionId(), this.anonymous);
        this.$set(this, 'commenters', students);
        return this.commenters;
      } catch(e) {
        return this.$emit('error', e);
      }
    },
    getDiscussionComments: _.throttle(async function getDiscussionCommentsThrottled(opts = {
      parentId: null,
      anonymous: this.anonymous,
    }) {
      try {
        if (!opts.pageSize) opts.pageSize = this.$constants.discussionCommentPageSize;
        if (
          this.discussionRole === 'anonymous'
          || this.isSearchingForComments
          || this.localIsProcessingData
        ) return this.comments;
        this.localIsProcessingData = true;
        const { data: comments } = await api.getDiscussionComments(opts);
        if (comments.length) this.populateComments(comments);
        return this.comments;
      } catch(e) {
        this.$emit('error', e);
        return e;
      } finally {
        this.localIsProcessingData = false;
      }
    }, 1000, {
      leading: true,
    }),
    getNextPage: _.throttle(function getNextPageThrottled() {
      if (!this.lastComment) return false;
      return this.getDiscussionComments({
        pageSize: this.$constants.discussionCommentPageSize,
        displayTimeGreaterThan: this.lastComment.displayTime,
        idGreaterThan: this.lastComment.id,
        parentId: null,
      });
    }, 1000, {
      leading: true,
    }),
    async populateComments(newComments) {
      this.comments = this.$_(newComments)
        .concat(this.comments)
        .uniqBy('id')
        .orderBy(
          [
            'displayTime',
            'id',
          ],
          [
            'ASC',
            'ASC',
          ],
        )
        .map((comment, commentIndex, allComments) => {
          if (!comment) return null;
          const replies = this.$_(this.$_.filter(allComments, (reply) => reply.parentId === comment.id))
            .concat(comment.replies)
            .uniqBy('id')
            .orderBy('created', 'ASC')
            .value();
          return {
            ...comment,
            replies,
            ...comment.clientsideOnly && {
              replyCount: replies.length,
            },
          };
        })
        .compact()
        .filter((comment) => !comment.parentId)
        .value();
      await this.$nextTick();
      return this.comments;
    },
    async getSearchResultComments(opts = { }) {
      try {
        if (
          this.discussionRole === 'anonymous'
          || this.localIsProcessingData
        ) return this.discussionRole === 'anonymous' ? this.commentsAndReplies : this.searchResultComments;
        if (this.haveSearchOptionsBeenChanged) this.searchResultComments = [];
        this.localIsProcessingData = true;
        const { data: comments } = await api.getDiscussionComments({
          ...opts,
          anonymous: this.anonymous,
          studentId: _.get(this, 'selectedCommenter.id', this.restrictDiscussionCommentsByStudentId),
          searchTerm: this.searchTerm,
          parentId: false,
          includeArchived: true,
          pageSize: this.$constants.discussionCommentPageSize,
          order: [['created', 'ASC']]
        });
        this.previousSearchResultOptions = _.cloneDeep({
          searchTerm: this.searchTerm,
          selectedCommenter: _.get(this, 'selectedCommenter'),
        });
        this.hasExecutedSearchQuery = true;
        this.showLoadMoreButtonForSearchResultComments = (comments.length === this.$constants.discussionCommentPageSize);
        this.populateSearchResultComments(comments);
        return this.searchResultComments;
      } catch(e) {
        return this.$emit('error', e);
      } finally {
        this.localIsProcessingData = false;
      }
    },
    getSearchResultCommentsDebounced: _.debounce(function getSearchResultCommentsDebounced() {
      this.getSearchResultComments();
    }, 1000),
    getNextSearchResultsPage() {
      this.getSearchResultComments({
        displayTimeGreaterThan: this.lastSearchResultComment.displayTime,
        idGreaterThan: this.lastSearchResultComment.id,
      });
    },
    getNextSearchResultsPageThrottled: _.throttle(function getNextSearchResultsPageThrottled() {
      if (!this.lastSearchResultComment) return false;
      return this.getSearchResultComments({
        displayTimeGreaterThan: this.lastSearchResultComment.displayTime,
        idGreaterThan: this.lastSearchResultComment.id,
      });
    }, 10000, {
      leading: true,
    }),
    populateSearchResultComments(newComments) {
      const comments = this.$_(this.discussionRole === 'anonymous' ? this.commentsAndReplies : this.searchResultComments)
        .concat(newComments)
        .uniqBy('id')
        .orderBy('displayTime')
        .map((comment) => ({
          ...comment,
          isSearchResult: true,
          replies: [],
          replyCount: 0,
        }))
        .compact()
        .value();
      if (this.discussionRole === 'anonymous') {
        this.$set(this, 'comments', comments);
      } else {
        this.$set(this, 'searchResultComments', comments);
      }
    },
    async refreshDiscussion() {
      try {
        if (
          this.discussionRole === 'anonymous'
          || this.isSearchingForComments
          || this.localIsProcessingData
        ) return this.commentsAndReplies;
        this.localIsProcessingData = true;
        const { data: comments } = await api.getDiscussionComments({
          anonymous: this.anonymous,
          displayTimeGreaterThan: this.comments[0].displayTime,
          displayTimeLessThan: this.lastComment.displayTime,
          idGreaterThan: this.highestCommentId,
        });
        this.populateComments(comments);
        return this.comments;
      } catch(e) {
        return this.$emit('error', e);
      } finally {
        this.localIsProcessingData = false;
      }
    },
    handleIntersectionObserver([event = {}] = []) {
      if (
        event.intersectionRatio !== 1
        || !this.isReadyForIntersectionObserverEvents
      ) {
        // The interactionObserver executes once when initialized so this is here to prevent making an unnecessary second call to get the initial discussion comments.
        this.isReadyForIntersectionObserverEvents = true;
        return false;
      }
      if (this.isSearchingForComments) {
        this.getNextSearchResultsPageThrottled();
      } else {
        this.getNextPage();
      }
      return true;
    },
    initIntersectionObserver() {
      const root = this.$el.querySelector('.discussion-comments-wrapper');
      if (!root) return false;
      this.observer = new IntersectionObserver(this.handleIntersectionObserver, {
        root,
      });
      this.observer.observe(this.$refs.intersectionObserverTarget);
      return true;
    },
    async deleteComment(commentToDelete) {
      try {
        if (this.localIsProcessingData) return false;
        this.localIsProcessingData = true;
        if (commentToDelete.clientsideOnly) {
          this.$set(
            this,
            'comments',
            this.comments
              .map((comment) => ({
                ...comment,
                ...comment.id === commentToDelete.id && {
                  deleted: true,
                },
                replies: comment.replies.map((reply) => ({
                  ...reply,
                  ...reply.id === commentToDelete.id && {
                    deleted: true,
                  },
                })),
              })),
          );
          return this.comments;
        }
        await api.deleteDiscussionComment(commentToDelete.id);
        const found = this.commentsAndReplies.find((comment) => comment.id === commentToDelete.id);
        if (!found) return false;
        found.deleted = true;
        return found;
      } catch(e) {
        return this.$emit('error', e);
      } finally {
        this.localIsProcessingData = false;
      }
    },
    async downloadComments() {
      try {
        if (this.localIsProcessingData) return false;
        this.localIsProcessingData = true;
        const { data } = await api.downloadDiscussionAttempts({
          anonymous: this.anonymous,
        });
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.style.display = 'none';
        link.setAttribute('download', `playposit-discussion-${this.localInteraction.id}`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        return data;
      } catch(e) {
        return this.$emit('error', e);
      } finally {
        this.localIsProcessingData = false;
      }
    },
  },
  watch: {
    searchTerm(n) {
      this.getSearchResultCommentsDebounced();
      if (this.discussionRole === 'anonymous') this.previousSearchResultOptions.searchTerm = n;
    },
    selectedCommenter(n) {
      if (this.discussionRole === 'anonymous') this.previousSearchResultOptions.selectedCommenter = n;
    },
    isSearchingForComments(n) {
      /*
        When we switch from showing the usual discussion comments to showing the ones that the user specifically searched/filtered for, we hide the main discussion comment elements. This results in .discussion-comments-wrapper's height collapsing which results in the intersection observer firing. Setting this value to false basically prevents that guaranteed intersection observer execution from firing off an unnecessary API call.
      */
      if (n) {
        this.isReadyForIntersectionObserverEvents = false;
        this.hasExecutedSearchQuery = (
          this.discussionRole === 'anonymous'
          || this.isExternallyRestrictingCommentsByStudentId
        );
      } else {
        this.searchResultComments = [];
      }
    },
  },
  async created() {
    this.getStudentsInSection();
    if (this.isSearchingForComments) this.getSearchResultComments();
    else this.getDiscussionComments();
    // this.getDiscussionComments();
  },
  async mounted() {
    await this.$nextTick();
    this.initIntersectionObserver();
  },
};
</script>

<style lang="scss">
  .discussion-answered {
    max-height: 50vh;
  }
  .discussion-progress-bar {
    position: absolute !important;
    top: 0;
    left: 0;
    border-top-left-radius: 2px;
    border-top-right-radius: 2px;
  }
  .discussion-comments-wrapper {
    overflow-y: auto;
  }
  .load-more-button-wrapper {
    min-height: 48px;
  }
</style>
